import { VIEWS } from './chat-screen-state';
import { escapeHtml } from '../../util/linkify';

export function searchActions() {
  return {
    openChatSearch() {
      this.exitSelectMode();
      this.searchSubmitTracked = false;
      this.searchQuery = '';
      this.searchUserResults = [];
      this.searchGroupResults = [];
      this.view = VIEWS.SEARCH;
      this.loadSearchSuggestions();
      this.$nextTick(() => this.$refs.chatSearchInput?.focus());
    },

    closeChatSearch() {
      this.searchQuery = '';
      this.searchUserResults = [];
      this.searchGroupResults = [];
      this.searchRequestId += 1;
      this.isSearchingChat = false;
      this.view = this.activeConversation ? VIEWS.CONVERSATION : VIEWS.LIST;
    },

    async loadSearchSuggestions() {
      this.isLoadingSearchSuggestions = true;
      try {
        const response = await this.$api({
          url: '/chat/search',
          method: 'GET'
        });
        this.searchSuggestedUsers = response.data.contacts || [];
      } catch (err) {
        console.error('Failed to load chat search suggestions:', err);
        this.searchSuggestedUsers = [];
      } finally {
        this.isLoadingSearchSuggestions = false;
      }
    },

    async searchChat() {
      const query = this.searchQuery.trim();
      const requestId = ++this.searchRequestId;

      if (!query) {
        this.searchUserResults = [];
        this.searchGroupResults = [];
        this.isSearchingChat = false;
        return;
      }

      this.isSearchingChat = true;
      try {
        const response = await this.$api({
          url: '/chat/search',
          method: 'GET',
          query: { query, limit: 5 }
        });
        // The input searches as the user types; count one submit per search session.
        if (!this.searchSubmitTracked) {
          this.searchSubmitTracked = true;
          this.trackChat('search', 'submit');
        }
        if (requestId === this.searchRequestId) {
          this.searchUserResults = response.data.contacts || [];
          this.searchGroupResults = (response.data.groups || []).map((group) => {
            const cached = this.getConversation(group.id) || {};
            return {
              ...group,
              ...cached,
              // Keep search fields that the conversation cache may not have.
              name: group.name,
              highlighted_name: group.highlighted_name,
              join_policy: group.join_policy || cached.join_policy,
              current_user_status: group.current_user_status || cached.current_user_status
            };
          });
        }
      } catch (err) {
        if (requestId === this.searchRequestId) {
          console.error('Failed to search chat contacts and groups:', err);
          this.searchUserResults = [];
          this.searchGroupResults = [];
        }
      } finally {
        if (requestId === this.searchRequestId) this.isSearchingChat = false;
      }
    },

    searchGroupIsJoined(group) {
      const status = group?.current_user_status;
      return status === 'active' || status === 'removed';
    },

    directConversationForUser(userId) {
      return this.conversations.find(
        (conversation) => conversation.conversation_type === 'direct'
          && conversation.other_participant?.id === userId
      );
    },

    async selectSearchUser(user) {
      const conversation = this.directConversationForUser(user.id);
      if (conversation) {
        this.openSearchConversation(conversation.id);
        return;
      }

      this.trackChat('search', 'select result');
      await this.openConversationWithUser(user.id, { source: 'search' });
    },

    async openSearchConversation(conversationId) {
      this.trackChat('search', 'select result');
      if (this.activeConversationId === conversationId) {
        this.view = VIEWS.CONVERSATION;
        return;
      }

      if (!this.getConversation(conversationId)) {
        await this.reloadConversation(conversationId);
      }

      if (this.getConversation(conversationId)) this.openConversation(conversationId, { source: 'search' });
    },

    clearChatSearch() {
      this.searchQuery = '';
      this.searchUserResults = [];
      this.searchGroupResults = [];
      this.searchRequestId += 1;
      this.isSearchingChat = false;
      this.$nextTick(() => this.$refs.chatSearchInput?.focus());
    },

    // Renders Elasticsearch-provided highlight HTML, falling back to an escaped plain name.
    highlightedSearchName(entity, fallbackName) {
      if (entity?.highlighted_name) return entity.highlighted_name;
      return escapeHtml(fallbackName || entity?.name || '');
    }
  };
}
